import { useTranslation } from 'react-i18next';

import { useWorkspaceMembers } from '@/entities/collab';
import { useSetCardArchived, useSetCardCompleted } from '@/entities/board';
import { describeRule, useRecurrence } from '@/entities/recurrence';
import { useErrorMessage } from '@/shared/i18n';
import { Button, Dialog, useToast } from '@/shared/ui';

import styles from './EventPreview.module.css';
import { describeWhen } from './format';
import type { CalendarEvent, UndatedTask } from './model';
import { type ItemStatus, statusLabel, statusOf } from './status';

interface Props {
  /** The calendar item: an event / deadline, or a task without a date. */
  item: { event: CalendarEvent } | { undated: UndatedTask };
  /** "Board" or "Board · Workspace". */
  source: string;
  /** The workspace of the item's board (to name participants), when known. */
  workspaceId: string | null;
  locale: string;
  canEdit: boolean;
  onClose: () => void;
  onOpenCard: () => void;
  /** The recurring-occurrence editor (this repeat or the entire series). */
  onEditOccurrence: () => void;
  onOpenSeriesSource?: (sourceCardId: string) => void;
}

function StatusChip({ status }: { status: ItemStatus }) {
  const { t } = useTranslation();
  return (
    <span className={styles.chip} data-status={status} data-testid="preview-status">
      {statusLabel(status, t)}
    </span>
  );
}

/**
 * A quick look at a calendar item without opening the card editor: when, where, who, state and
 * the repeat rule. A popover-sized dialog on a desktop, a bottom sheet on a phone (the dialog
 * does that itself). The full editor, the occurrence editor and completion are one tap away.
 */
export function EventPreview({
  item,
  source,
  workspaceId,
  locale,
  canEdit,
  onClose,
  onOpenCard,
  onEditOccurrence,
  onOpenSeriesSource,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const message = useErrorMessage();
  const event = 'event' in item ? item.event : null;
  const card = event ? event.card : 'undated' in item ? item.undated.card : null;
  const boardId = event ? event.boardId : 'undated' in item ? item.undated.boardId : '';
  const occurrence = event?.occurrence;
  const members = useWorkspaceMembers(workspaceId ?? '', workspaceId !== null);
  const rule = useRecurrence(occurrence?.source_card_id ?? '', occurrence !== undefined);
  const complete = useSetCardCompleted(boardId);
  const unarchive = useSetCardArchived(boardId);
  if (!card) return null;
  const status = event ? statusOf(event) : card.completed_at !== null ? 'completed' : null;
  const names = new Map(
    (members.data ?? []).map((member) => [member.user_id, member.display_name]),
  );
  const people = card.assignee_ids.map((id) => names.get(id)).filter(Boolean);
  const when = event ? describeWhen(event, locale, t) : t('calendar.preview.noDate');
  const archived = card.archived_at !== null && card.archived_at !== undefined;

  return (
    <Dialog title={card.title} onClose={onClose}>
      <dl className={styles.facts} data-testid="event-preview">
        <div className={styles.row}>
          <dt>{t('calendar.preview.when')}</dt>
          <dd data-testid="preview-when">{when}</dd>
        </div>
        {(status || archived) && (
          <div className={styles.row}>
            <dt>{t('calendar.preview.status')}</dt>
            <dd>
              {status && <StatusChip status={status} />}
              {archived && (
                <span
                  className={styles.chip}
                  data-status="archived"
                  data-testid="preview-archived-chip"
                >
                  {t('calendar.archivedTag')}
                </span>
              )}
            </dd>
          </div>
        )}
        {source && (
          <div className={styles.row}>
            <dt>{t('calendar.preview.board')}</dt>
            <dd>{source}</dd>
          </div>
        )}
        {people.length > 0 && (
          <div className={styles.row}>
            <dt>{t('calendar.preview.participants')}</dt>
            <dd>{people.join(', ')}</dd>
          </div>
        )}
        {occurrence && (
          <div className={styles.row}>
            <dt>{t('calendar.preview.repeats')}</dt>
            <dd data-testid="preview-recurrence">
              {rule.data?.rule
                ? describeRule(rule.data.rule, t, locale)
                : rule.isPending
                  ? t('calendar.preview.repeatsLoading')
                  : '—'}
            </dd>
          </div>
        )}
      </dl>
      {occurrence && <p className={styles.note}>{t('calendar.preview.upcomingNote')}</p>}
      {archived && (
        <p className={styles.note} data-testid="preview-archived">
          {t('calendar.archivedNote')}
        </p>
      )}
      <div className={styles.actions}>
        {occurrence ? (
          <Button variant="primary" onClick={onEditOccurrence}>
            {canEdit ? t('calendar.preview.editOccurrence') : t('calendar.preview.openCard')}
          </Button>
        ) : (
          <Button variant="primary" onClick={onOpenCard}>
            {t('calendar.preview.openCard')}
          </Button>
        )}
        {occurrence && canEdit && onOpenSeriesSource && (
          <Button
            variant="ghost"
            onClick={() => {
              onOpenSeriesSource(occurrence.source_card_id);
            }}
          >
            {t('calendar.preview.openSeriesSource')}
          </Button>
        )}
        {canEdit && archived && (
          // restoring is an explicit choice: opening an archived task never returns it by itself
          <Button
            disabled={unarchive.isPending}
            data-testid="return-from-archive"
            onClick={() => {
              unarchive.mutate(
                { cardId: card.id, archived: false },
                {
                  onSuccess: onClose,
                  onError: (error) => {
                    toast.show({ message: message(error) });
                  },
                },
              );
            }}
          >
            {t('calendar.returnToBoard')}
          </Button>
        )}
        {canEdit && !occurrence && !archived && (
          <Button
            disabled={complete.isPending}
            onClick={(clickEvent) => {
              complete.mutate(
                {
                  cardId: card.id,
                  completed: card.completed_at === null,
                  anchor: clickEvent.currentTarget,
                },
                {
                  onSuccess: onClose,
                  onError: (error) => {
                    toast.show({ message: message(error) });
                  },
                },
              );
            }}
          >
            {card.completed_at === null
              ? t('calendar.preview.markDone')
              : t('calendar.preview.reopen')}
          </Button>
        )}
      </div>
    </Dialog>
  );
}
